import { ApiResponse } from "../interfaces/Api";
import {
  TCalendarSummary,
  TLeaveSummary,
  TReimbursements,
  TSalary,
  TSalaryFormData,
  TSalarySummary
} from "../interfaces/SalaryInterface";
import { Delete, Get, Patch, Post } from "./apiHelper";

const payrollRoot = "/api/payroll";

// POST /payroll
export const postSalary = async (data: TSalaryFormData): Promise<ApiResponse<TSalary>> => {
  const response = await Post(`${payrollRoot}`, data as unknown as Record<string, unknown>, true);
  return response.data as ApiResponse<TSalary>;
};

// POST /payroll/bulk-payroll
export const postBulkSalary = async (data: TSalaryFormData[]): Promise<ApiResponse<TSalary>> => {
  const response = await Post(
    `${payrollRoot}/bulk`,
    data as unknown as Record<string, unknown>,
    true
  );
  return response.data as ApiResponse<TSalary>;
};

// PATCH /payroll/:id
export const patchSalary = async ({
  id,
  data
}: {
  id: number | string;
  data: Partial<TSalaryFormData>;
}): Promise<ApiResponse<TSalary>> => {
  const response = await Patch(
    `${payrollRoot}/${id}`,
    data as unknown as Record<string, TSalary>,
    true
  );
  return response.data as ApiResponse<TSalary>;
};

// DELETE /payroll/:id
export const deleteSalary = async (id: number | string): Promise<ApiResponse<null>> => {
  const response = await Delete(`${payrollRoot}/${id}`, true, {});
  return response.data as ApiResponse<null>;
};

// GET /payroll/:id
export const getSalary = async (id: number | string): Promise<ApiResponse<TSalary>> => {
  const response = await Get(`${payrollRoot}/${id}`, true, {});
  return response.data as ApiResponse<TSalary>;
};

// GET /payroll
export const getSalaries = async ({
  employeeIds,
  serviceProviderIds,
  resourceCentreId,
  payrollYear,
  payrollMonth,
  fromDate,
  untilDate
}: {
  employeeIds?: number[];
  serviceProviderIds?: number[];
  resourceCentreId: number;
  payrollYear?: number;
  payrollMonth?: number;
  fromDate?: string;
  untilDate?: string;
}): Promise<ApiResponse<Array<TSalary>>> => {
  const queryParams = new URLSearchParams();
  queryParams.append("resourceCentreId", String(resourceCentreId));

  if (fromDate !== undefined) {
    queryParams.append("fromDate", fromDate);
  }

  if (untilDate !== undefined) {
    queryParams.append("untilDate", untilDate);
  }

  if (payrollYear !== undefined) {
    queryParams.append("payrollYear", String(payrollYear));
  }

  if (payrollMonth !== undefined) {
    queryParams.append("payrollMonth", String(payrollMonth));
  }

  if (employeeIds?.length) {
    queryParams.append("employeeIds", employeeIds.join(","));
  }

  if (serviceProviderIds?.length) {
    queryParams.append("serviceProviderIds", serviceProviderIds.join(","));
  }

  const response = await Get(`${payrollRoot}?${queryParams.toString()}`, true);
  return response.data as ApiResponse<Array<TSalary>>;
};

// GET /payroll/employeeFiscalSalaries
export const getEmployeeFiscalSalaries = async ({
  employeeId,
  serviceProviderId,
  payrollYear,
  payrollMonth,
  rcId
}: {
  employeeId?: number;
  serviceProviderId?: number;
  payrollYear: number;
  payrollMonth: number;
  rcId: number;
}): Promise<ApiResponse<Array<TSalary>>> => {
  const queryParams = new URLSearchParams();

  // Optional query parameters
  if (employeeId !== undefined) {
    queryParams.append("employeeId", String(employeeId)); // ensure string
  }

  if (serviceProviderId !== undefined) {
    queryParams.append("serviceProviderId", String(serviceProviderId));
  }

  // Required query parameters
  queryParams.append("payrollYear", String(payrollYear));
  queryParams.append("payrollMonth", String(payrollMonth));
  queryParams.append("rcId", String(rcId));

  const response = await Get(
    `${payrollRoot}/employeeFiscalSalaries?${queryParams.toString()}`,
    true
  );

  return response.data as ApiResponse<Array<TSalary>>;
};

// POST /salaries/calculate
export const calculateEmployeePayrollSummary = async (
  data: Partial<TSalary>
): Promise<
  ApiResponse<{
    salarySummary: TSalarySummary;
    leaveSummary: TLeaveSummary;
    calendarSummary: TCalendarSummary;
    reimbursements: TReimbursements;
  }>
> => {
  const response = await Post(
    `${payrollRoot}/calculate`,
    data as unknown as Record<string, unknown>,
    true
  );
  return response.data as ApiResponse<{
    salarySummary: TSalarySummary;
    leaveSummary: TLeaveSummary;
    calendarSummary: TCalendarSummary;
    reimbursements: TReimbursements;
  }>;
};
